import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useGame } from '../contexts/GameContext';

// Import the actual images
import socialCreditImg from '../assets/images/up.jpg';
import trumpWinningImg from '../assets/images/fries.jpeg';

// Hardcoded audio paths
const SOCIAL_CREDIT_AUDIO_PATH = '/sounds/socialscore.mp3';
const TRUMP_AUDIO_PATH = '/sounds/tiredofwinning.mp3';

const AnimationContainer = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 999;
  opacity: ${props => (props.show ? 1 : 0)};
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  transition: opacity 0.4s ease, visibility 0.4s ease;
  backdrop-filter: blur(5px);
`;

const AnimationContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  background-color: var(--card-bg-color);
  border-radius: var(--border-radius);
  text-align: center;
  max-width: 550px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  border: var(--card-border);
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  
  @keyframes popIn {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const MemeImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  transform: scale(0.95);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1);
  }
`;

const ScoreChange = styled.div<{ isPositive: boolean }>`
  font-size: 42px;
  font-weight: 800;
  color: ${props => (props.isPositive ? 'var(--success-color)' : 'var(--error-color)')};
  margin: 15px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  padding: 5px 20px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => (props.isPositive ? 'var(--success-color)' : 'var(--error-color)')};
    border-radius: 3px;
  }
`;

const FeedbackText = styled.div`
  font-size: 24px;
  margin: 15px 0;
  color: var(--text-primary);
  font-weight: 600;
  line-height: 1.4;
  max-width: 450px;
`;

// Add these sparkle elements for positive feedback
const Sparkle = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  background-color: gold;
  border-radius: 50%;
  filter: blur(1px);
  opacity: 0;
  pointer-events: none;
  animation: sparkle 1.5s ease-in-out infinite;
  
  @keyframes sparkle {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }
`;

// Simple Audio component that handles playing audio 
const SoundEffect = ({ soundType }: { soundType: 'success' | 'failure' }) => {
  useEffect(() => {
    const audioElement = new Audio(soundType === 'success' ? SOCIAL_CREDIT_AUDIO_PATH : TRUMP_AUDIO_PATH);
    // Set maximum play duration (5 seconds for social credit, 4 seconds for trump)
    const maxDuration = soundType === 'success' ? 5000 : 4000;
    
    const playSound = () => {
      console.log(`Playing ${soundType} sound for ${maxDuration/1000} seconds`);
      audioElement.volume = 1.0;
      
      // Try multiple approaches to play audio
      
      // Method 1: Standard play with catch
      const playPromise = audioElement.play();
      
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // Set a timeout to stop the sound after specified duration
          setTimeout(() => {
            audioElement.pause();
            audioElement.currentTime = 0;
          }, maxDuration);
        }).catch(error => {
          console.error(`Error playing ${soundType} sound:`, error);
          
          // Method 2: If autoplay fails, try to play after a user interaction
          const handleUserInteraction = () => {
            audioElement.play().then(() => {
              // Set a timeout to stop the sound after specified duration
              setTimeout(() => {
                audioElement.pause();
                audioElement.currentTime = 0;
              }, maxDuration);
            }).catch(err => 
              console.error(`Still cannot play sound after user interaction: ${err}`)
            );
            
            // Clean up event listeners
            ['click', 'touchstart', 'keydown'].forEach(event => {
              document.removeEventListener(event, handleUserInteraction);
            });
          };
          
          // Add event listeners for user interaction
          ['click', 'touchstart', 'keydown'].forEach(event => {
            document.addEventListener(event, handleUserInteraction, { once: true });
          });
          
          // Method 3: Alternative approach for iOS
          if (error.name === 'NotAllowedError') {
            // Create a visible but small button that responds to user interaction
            const tempButton = document.createElement('button');
            tempButton.textContent = 'Enable Sound';
            tempButton.style.position = 'fixed';
            tempButton.style.bottom = '10px';
            tempButton.style.right = '10px';
            tempButton.style.zIndex = '9999';
            
            tempButton.onclick = () => {
              const playWithLimit = audioElement.play();
              if (playWithLimit) {
                playWithLimit.then(() => {
                  // Set a timeout to stop the sound after specified duration
                  setTimeout(() => {
                    audioElement.pause();
                    audioElement.currentTime = 0;
                  }, maxDuration);
                });
              }
              document.body.removeChild(tempButton);
            };
            
            document.body.appendChild(tempButton);
            
            // Auto-remove after 5 seconds
            setTimeout(() => {
              if (document.body.contains(tempButton)) {
                document.body.removeChild(tempButton);
              }
            }, 5000);
          }
        });
      }
    };
    
    // Start playing after a small delay
    const timer = setTimeout(playSound, 100);
    
    return () => {
      clearTimeout(timer);
      audioElement.pause();
      audioElement.currentTime = 0;
      
      // Clean up any remaining event listeners
      ['click', 'touchstart', 'keydown'].forEach(event => {
        document.removeEventListener(event, () => {});
      });
    };
  }, [soundType]);
  
  return null;
};

const renderSparkles = (isPositive: boolean) => {
  if (!isPositive) return null;
  
  const sparkles = [];
  for (let i = 0; i < 15; i++) {
    const delay = Math.random() * 2;
    const top = Math.random() * 100;
    const left = Math.random() * 100;
    
    sparkles.push(
      <Sparkle 
        key={i}
        style={{
          top: `${top}%`,
          left: `${left}%`,
          animationDelay: `${delay}s`,
        }}
      />
    );
  }
  
  return sparkles;
};

const FeedbackAnimation: React.FC = () => {
  const { showFeedback, isAnswerCorrect } = useGame();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (showFeedback) {
      setVisible(true);
      console.log('Showing feedback for:', isAnswerCorrect ? 'Social Credit' : 'Trump');
      
      // Hide animation after 3 seconds
      const timer = setTimeout(() => {
        setVisible(false);
      }, 3000);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [showFeedback, isAnswerCorrect]);

  if (!showFeedback) return null;

  return (
    <AnimationContainer show={visible}>
      {/* Sound effect component */}
      {isAnswerCorrect ? 
        <SoundEffect soundType="success" /> : 
        <SoundEffect soundType="failure" />
      }
      
      <AnimationContent>
        {renderSparkles(isAnswerCorrect || false)}
        <MemeImage 
          src={isAnswerCorrect ? socialCreditImg : trumpWinningImg} 
          alt={isAnswerCorrect ? "Social Credit Meme" : "Trump Winning Meme"} 
        />
        <ScoreChange isPositive={isAnswerCorrect || false}>
          {isAnswerCorrect ? '+15 Tariff Score' : 'Winning Too Much'}
        </ScoreChange>
        <FeedbackText>
          {isAnswerCorrect 
            ? "做得好! (Well done!)" 
            : "We're winning so much that you are tired of winning!"}
        </FeedbackText>
      </AnimationContent>
    </AnimationContainer>
  );
};

export default FeedbackAnimation; 